import { ArrayLikeUtils } from './ArrayLikeUtils';
import { DataStorePaths } from './DataStorePaths';
import { IDataStore } from './IDataStore';

/**
 * Animation scenario enum matching C++ AnimationScenarioType values.
 * C++ Reference: tabanim/main/sceneanimation/AnimationScenario.h
 */
export enum AnimationScenario {
  Table = 'Table',
  TableDisabled = 'TableDisabled',
  Scatterplot = 'Scatterplot',
  ScatterplotDisabled = 'ScatterplotDisabled',
  MapWithMapMarks = 'MapWithMapMarks',
  Map = 'Map',
  MapDisabled = 'MapDisabled',
  VerticalAreaChart = 'VerticalAreaChart',
  VerticalChart = 'VerticalChart',
  VerticalChartDisabled = 'VerticalChartDisabled',
  HorizontalAreaChart = 'HorizontalAreaChart',
  HorizontalChart = 'HorizontalChart',
  HorizontalChartDisabled = 'HorizontalChartDisabled',
  VerticalScatterbar = 'VerticalScatterbar',
  VerticalScatterbarDisabled = 'VerticalScatterbarDisabled',
  HorizontalScatterbar = 'HorizontalScatterbar',
  HorizontalScatterbarDisabled = 'HorizontalScatterbarDisabled',
  PieChart = 'PieChart',
  Other = 'Other',
}

/**
 * PrimitiveType values from the datastore's MarkLayoutData.Parameters.PrimitiveType.
 * - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:162 (primTypeMap)
 * - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:176 (GetAnimationScenarioPrimType)
 */
const enum PrimitiveType {
  Shape = 0,
  Line = 1,
  Bar = 2,
  PolarBar = 3,
  Text = 4,
  Label = 5,
  Pie = 6,
  Area = 7,
  Polygon = 8,
  Multipolygon = 9,
  Square = 10,
  Heatmap = 11,
}

export type AnimationScenarioFeatureFlags = {
  animatePieCharts: boolean;
  markAnimationOfPolygons: boolean;
};

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:15 (MarkAnimationOfPolygons)
// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:20 (AnimatePieCharts)
const defaultAnimationScenarioFeatureFlags: AnimationScenarioFeatureFlags = {
  animatePieCharts: true,
  markAnimationOfPolygons: false,
};

let animationScenarioFeatureFlags: AnimationScenarioFeatureFlags = { ...defaultAnimationScenarioFeatureFlags };

const enum AxisRangeType {
  // - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:150 (ComputeAnimationScenarioAxisType)
  FixedRangeOrdinal = 10,
}

// - monolith/modules/platform/tabanim/public/sceneanimation/AnimationScenarioEnum.h:43 (AnimationScenarioPrimType)
const enum AnimationScenarioPrimType {
  None = 0,
  Shape = 1 << 0,
  Square = 1 << 1,
  Line = 1 << 2,
  Bar = 1 << 3,
  Area = 1 << 4,
  Polygon = 1 << 5,
  PolarBar = 1 << 6,
  Text = 1 << 7,
  Label = 1 << 8,
  Pie = 1 << 9,
  Multipolygon = 1 << 10,
  Heatmap = 1 << 11,
  All = 0xffff,
}

// - monolith/modules/platform/tabanim/public/sceneanimation/AnimationScenarioEnum.h:34 (AnimationScenarioAxisType)
const enum AnimationScenarioAxisType {
  None = 1 << 0,
  Ordinal = 1 << 1,
  Quantitative = 1 << 2,
  Map = 1 << 3,
  All = 0xffff,
}

type AnimationScenarioDescriptor = {
  xAxisMask: AnimationScenarioAxisType;
  yAxisMask: AnimationScenarioAxisType;
  primTypeMask: AnimationScenarioPrimType;
  enterMarksAnimationEnabled: boolean;
  moveMarksAnimationEnabled: boolean;
  exitMarksAnimationEnabled: boolean;
};

type AnimationScenarioTypePair = {
  type: AnimationScenario;
  descriptor: AnimationScenarioDescriptor;
};

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:98 (AnimationScenarioAxis_All & ~AnimationScenarioAxis_Map)
const NON_MAP_AXIS_MASK = AnimationScenarioAxisType.All & ~AnimationScenarioAxisType.Map;

export function setAnimationScenarioFeatureFlags(flags: Partial<AnimationScenarioFeatureFlags>): void {
  animationScenarioFeatureFlags = {
    ...animationScenarioFeatureFlags,
    ...flags,
  };
}

export function resetAnimationScenarioFeatureFlags(): void {
  animationScenarioFeatureFlags = { ...defaultAnimationScenarioFeatureFlags };
}

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:10 (EnabledPrimMask)
function enabledPrimMask(): AnimationScenarioPrimType {
  let enabledPrims =
    AnimationScenarioPrimType.Shape |
    AnimationScenarioPrimType.Square |
    AnimationScenarioPrimType.Bar |
    AnimationScenarioPrimType.Line |
    AnimationScenarioPrimType.Area |
    AnimationScenarioPrimType.Heatmap;

  if (animationScenarioFeatureFlags.markAnimationOfPolygons) {
    enabledPrims |= AnimationScenarioPrimType.Polygon;
  }

  if (animationScenarioFeatureFlags.animatePieCharts) {
    enabledPrims |= AnimationScenarioPrimType.Pie;
  }

  return enabledPrims as AnimationScenarioPrimType;
}

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:27 (DisabledPrimMask)
function disabledPrimMask(): AnimationScenarioPrimType {
  let disabledPrims =
    AnimationScenarioPrimType.PolarBar |
    AnimationScenarioPrimType.Text |
    AnimationScenarioPrimType.Label |
    AnimationScenarioPrimType.Multipolygon;

  if (!animationScenarioFeatureFlags.markAnimationOfPolygons) {
    disabledPrims |= AnimationScenarioPrimType.Polygon;
  }

  if (!animationScenarioFeatureFlags.animatePieCharts) {
    disabledPrims |= AnimationScenarioPrimType.Pie;
  }

  return disabledPrims as AnimationScenarioPrimType;
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:176 (GetAnimationScenarioPrimType)
function getAnimationScenarioPrimType(primType: number): AnimationScenarioPrimType {
  switch (primType) {
    case PrimitiveType.Shape:
      return AnimationScenarioPrimType.Shape;
    case PrimitiveType.Line:
      return AnimationScenarioPrimType.Line;
    case PrimitiveType.Bar:
      return AnimationScenarioPrimType.Bar;
    case PrimitiveType.PolarBar:
      return AnimationScenarioPrimType.PolarBar;
    case PrimitiveType.Text:
      return AnimationScenarioPrimType.Text;
    case PrimitiveType.Label:
      return AnimationScenarioPrimType.Label;
    case PrimitiveType.Pie:
      return AnimationScenarioPrimType.Pie;
    case PrimitiveType.Area:
      return AnimationScenarioPrimType.Area;
    case PrimitiveType.Polygon:
      return AnimationScenarioPrimType.Polygon;
    case PrimitiveType.Multipolygon:
      return AnimationScenarioPrimType.Multipolygon;
    case PrimitiveType.Square:
      return AnimationScenarioPrimType.Square;
    case PrimitiveType.Heatmap:
      return AnimationScenarioPrimType.Heatmap;
    default:
      return AnimationScenarioPrimType.None;
  }
}

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:45 (GetAnimationScenarioTypePairVector)
// Note: ordering matters and is preserved to match native first-match behavior.
function getAnimationScenarioTypePairVector(): AnimationScenarioTypePair[] {
  const pieChartsEnabled = animationScenarioFeatureFlags.animatePieCharts;
  const enabledMask = enabledPrimMask();
  const disabledMask = disabledPrimMask();

  return [
    ...(pieChartsEnabled
      ? [
          {
            type: AnimationScenario.PieChart,
            descriptor: {
              xAxisMask: AnimationScenarioAxisType.All,
              yAxisMask: AnimationScenarioAxisType.All,
              primTypeMask: AnimationScenarioPrimType.Pie,
              enterMarksAnimationEnabled: true,
              moveMarksAnimationEnabled: true,
              exitMarksAnimationEnabled: true,
            },
          },
        ]
      : []),
    {
      type: AnimationScenario.Table,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Ordinal,
        yAxisMask: AnimationScenarioAxisType.Ordinal,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.TableDisabled,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Ordinal,
        yAxisMask: AnimationScenarioAxisType.Ordinal,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.Scatterplot,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Quantitative,
        yAxisMask: AnimationScenarioAxisType.Quantitative,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.ScatterplotDisabled,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Quantitative,
        yAxisMask: AnimationScenarioAxisType.Quantitative,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.MapWithMapMarks,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Map,
        yAxisMask: AnimationScenarioAxisType.Map,
        primTypeMask: AnimationScenarioPrimType.Multipolygon,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.Map,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Map,
        yAxisMask: AnimationScenarioAxisType.Map,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.MapDisabled,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Map,
        yAxisMask: AnimationScenarioAxisType.Map,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.HorizontalAreaChart,
      descriptor: {
        xAxisMask: NON_MAP_AXIS_MASK,
        yAxisMask: AnimationScenarioAxisType.Ordinal,
        primTypeMask: AnimationScenarioPrimType.Area,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.HorizontalChart,
      descriptor: {
        xAxisMask: NON_MAP_AXIS_MASK,
        yAxisMask: AnimationScenarioAxisType.Ordinal,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.HorizontalChartDisabled,
      descriptor: {
        xAxisMask: NON_MAP_AXIS_MASK,
        yAxisMask: AnimationScenarioAxisType.Ordinal,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.VerticalAreaChart,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Ordinal,
        yAxisMask: NON_MAP_AXIS_MASK,
        primTypeMask: AnimationScenarioPrimType.Area,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.VerticalChart,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Ordinal,
        yAxisMask: NON_MAP_AXIS_MASK,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.VerticalChartDisabled,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Ordinal,
        yAxisMask: NON_MAP_AXIS_MASK,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.HorizontalScatterbar,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Quantitative,
        yAxisMask: AnimationScenarioAxisType.None,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.HorizontalScatterbarDisabled,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.Quantitative,
        yAxisMask: AnimationScenarioAxisType.None,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.VerticalScatterbar,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.None,
        yAxisMask: AnimationScenarioAxisType.Quantitative,
        primTypeMask: enabledMask,
        enterMarksAnimationEnabled: true,
        moveMarksAnimationEnabled: true,
        exitMarksAnimationEnabled: true,
      },
    },
    {
      type: AnimationScenario.VerticalScatterbarDisabled,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.None,
        yAxisMask: AnimationScenarioAxisType.Quantitative,
        primTypeMask: disabledMask,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
    {
      type: AnimationScenario.Other,
      descriptor: {
        xAxisMask: AnimationScenarioAxisType.All,
        yAxisMask: AnimationScenarioAxisType.All,
        primTypeMask: AnimationScenarioPrimType.All,
        enterMarksAnimationEnabled: false,
        moveMarksAnimationEnabled: false,
        exitMarksAnimationEnabled: false,
      },
    },
  ];
}

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:187 (GetAnimationScenarioType)
// Match logic mirrors native axis/primitive mask checks.
function getAnimationScenarioType(scenario: AnimationScenarioDescriptor): AnimationScenario {
  for (const typeScenarioPair of getAnimationScenarioTypePairVector()) {
    const curScenario = typeScenarioPair.descriptor;
    const xAxisMatches = curScenario.xAxisMask === AnimationScenarioAxisType.All || (curScenario.xAxisMask & scenario.xAxisMask) !== 0;
    const yAxisMatches = curScenario.yAxisMask === AnimationScenarioAxisType.All || (curScenario.yAxisMask & scenario.yAxisMask) !== 0;
    const primTypeMatches =
      curScenario.primTypeMask === AnimationScenarioPrimType.All || (curScenario.primTypeMask & scenario.primTypeMask) !== 0;

    if (xAxisMatches && yAxisMatches && primTypeMatches) {
      return typeScenarioPair.type;
    }
  }

  return AnimationScenario.Other;
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:109 (X axis pane index lookup)
// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:134 (Y axis pane index lookup)
function getPaneIndex(ds: IDataStore, paneId: string): number {
  return DataStorePaths.paneKeys(ds).indexOf(paneId);
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:29 (MapDomainBottom)
// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:46 (MapDomainTop)
// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:63 (MapDomainLeft)
// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:80 (MapDomainRight)
function getPaneExtentValue(ds: IDataStore, paneId: string, path: string): number {
  const paneIndex = getPaneIndex(ds, paneId);
  if (paneIndex < 0) {
    return 0;
  }
  const values = ArrayLikeUtils.toArray<number>(ds.getValue(path));
  if (paneIndex >= values.length) {
    return 0;
  }
  const value = values[paneIndex];
  return typeof value === 'number' ? value : 0;
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:150 (ComputeAnimationScenarioAxisType)
function getAxisTypeForRangeType(rangeType: number): AnimationScenarioAxisType {
  return rangeType === AxisRangeType.FixedRangeOrdinal ? AnimationScenarioAxisType.Ordinal : AnimationScenarioAxisType.Quantitative;
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:98 (XAxisType)
function getXAxisType(ds: IDataStore, paneId: string): AnimationScenarioAxisType {
  if (
    getPaneExtentValue(ds, paneId, DataStorePaths.MAP_DOMAIN_LEFT) !== 0 ||
    getPaneExtentValue(ds, paneId, DataStorePaths.MAP_DOMAIN_RIGHT) !== 0
  ) {
    return AnimationScenarioAxisType.Map;
  }

  const paneIndex = getPaneIndex(ds, paneId);
  if (paneIndex < 0) {
    return AnimationScenarioAxisType.None;
  }

  const paneColumns = ArrayLikeUtils.toArray<number>(ds.getValue('Viewpoint.PerPaneExtents.pane_col'));
  if (paneIndex >= paneColumns.length) {
    return AnimationScenarioAxisType.None;
  }

  const paneColumnIndex = paneColumns[paneIndex];
  const axisRangeTypes = ArrayLikeUtils.toArray<number>(ds.getValue('AxisOutput.XAxisTable.RangeType'));
  if (typeof paneColumnIndex !== 'number' || paneColumnIndex < 0 || paneColumnIndex >= axisRangeTypes.length) {
    return AnimationScenarioAxisType.None;
  }

  return getAxisTypeForRangeType(axisRangeTypes[paneColumnIndex]);
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:124 (YAxisType)
function getYAxisType(ds: IDataStore, paneId: string): AnimationScenarioAxisType {
  if (
    getPaneExtentValue(ds, paneId, DataStorePaths.MAP_DOMAIN_BOTTOM) !== 0 ||
    getPaneExtentValue(ds, paneId, DataStorePaths.MAP_DOMAIN_TOP) !== 0
  ) {
    return AnimationScenarioAxisType.Map;
  }

  const paneIndex = getPaneIndex(ds, paneId);
  if (paneIndex < 0) {
    return AnimationScenarioAxisType.Quantitative;
  }

  const paneRows = ArrayLikeUtils.toArray<number>(ds.getValue('Viewpoint.PerPaneExtents.pane_row'));
  if (paneIndex >= paneRows.length) {
    return AnimationScenarioAxisType.Quantitative;
  }

  const paneRowIndex = paneRows[paneIndex];
  const axisRangeTypes = ArrayLikeUtils.toArray<number>(ds.getValue('AxisOutput.YAxisTable.RangeType'));
  if (typeof paneRowIndex !== 'number' || paneRowIndex < 0 || paneRowIndex >= axisRangeTypes.length) {
    return AnimationScenarioAxisType.Quantitative;
  }

  return getAxisTypeForRangeType(axisRangeTypes[paneRowIndex]);
}

/**
 * Best-effort mapping from primType to AnimationScenario.
 * - WASM authoritative path is DataStorePaneStateScenarioAnalyzer::ComputeScenarioTypeForPane.
 * - This helper is retained as a coarse fallback/testing utility only.
 */
export function scenarioFromPrimType(primType: number): AnimationScenario {
  switch (getAnimationScenarioPrimType(primType)) {
    case AnimationScenarioPrimType.Shape:
    case AnimationScenarioPrimType.Line:
      return AnimationScenario.Scatterplot;
    case AnimationScenarioPrimType.Bar:
    case AnimationScenarioPrimType.Square:
    case AnimationScenarioPrimType.Heatmap:
      return AnimationScenario.VerticalChart;
    case AnimationScenarioPrimType.Area:
      return AnimationScenario.VerticalAreaChart;
    case AnimationScenarioPrimType.Pie:
      return AnimationScenario.PieChart;
    default:
      return AnimationScenario.Other;
  }
}

// - monolith/modules/platform/tabanim/main/sceneanimation/DataStorePaneStateScenarioAnalyzer.cpp:187 (ComputeScenarioTypeForPane)
// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:187 (GetAnimationScenarioType)
export function scenarioFromPane(ds: IDataStore, paneId: string): AnimationScenario {
  return getAnimationScenarioType({
    xAxisMask: getXAxisType(ds, paneId),
    yAxisMask: getYAxisType(ds, paneId),
    primTypeMask: getAnimationScenarioPrimType(DataStorePaths.primitiveType(ds, paneId)),
    enterMarksAnimationEnabled: true,
    moveMarksAnimationEnabled: true,
    exitMarksAnimationEnabled: true,
  });
}

// - monolith/modules/platform/tabanim/main/sceneanimation/AnimationScenario.cpp:27 (DisabledPrimMask)
// Disabled scenarios are those with enter/move/exit mark animation all off.
export function isMarkAnimationDisabledScenario(scenario: AnimationScenario): boolean {
  const typePair = getAnimationScenarioTypePairVector().find((pair) => pair.type === scenario);
  return (
    typePair !== undefined &&
    !typePair.descriptor.enterMarksAnimationEnabled &&
    !typePair.descriptor.moveMarksAnimationEnabled &&
    !typePair.descriptor.exitMarksAnimationEnabled
  );
}
